import { useMatches, useSearchParams } from "react-router";
import { useCallback, useEffect, useState } from "react";
import useLocale from "~/hook/useLocale";
import useStopCurrentRunningVideoAudio from "~/hook/useStopCurrentRunningVideoAudio";
import { getSession } from "~/lib/helperFuncation/sessionStorageService";
import { changeIsAudioPlayState } from "~/lib/store/slice/audioSlice";
import {
  onPauseVideo,
  onPlayVideo,
  setCurrentVideo,
  setCurrentVideoPosition,
  setFullScreen,
  setIsMute,
  setVideoQueue,
  setYoutubeVideoIsPlay,
  videoDataType,
} from "~/lib/store/slice/youtubePlayerSlice";
import { useAppDispatch, useAppSelector } from "~/lib/store/store";
import { VIDEO_PLAYER } from "~/utils/constant";
import DesktopVideoPopup from "./DesktopVideoPopup";
import MobileVideoPopup from "./MobileVideoPopup";

type CurrentVideo = {
  videoId: string;
  from: string;
};

function GlobalVideoPlayer() {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [currentVideo, setVideo] = useState<CurrentVideo | null>(null);
  const {
    isVideoPlay,
    youtubeVideoIsPlay,
    currentVideo: currentVideoFromStore,
    isMute,
    pipMode,
    isFullScreen,
  } = useAppSelector((state) => state.youtubeVideo);
  const { isPlaying } = useAppSelector((state) => state.nityNiyam);

  const [isMobile, setIsMobile] = useState(false);

  const { removeVideoPlay } = useStopCurrentRunningVideoAudio();

  const playListQueueData: videoDataType[] = getSession(VIDEO_PLAYER.VIDEO_QUEUE) || [];
  const currentPlayVideo: videoDataType = getSession(VIDEO_PLAYER.CURRENT_VIDEO) || null;

  const routesToHidePlayer = ["watch", "$lang.live.($slug)", "$lang.event-images.$url_slug._index"];
  const threeSegmentRoutes = ["$lang.$video_category.$slug.$(playlist_slug).$(last_slug)"];

  const matches = useMatches();
  const currentRouteId = matches[matches.length - 1].id;
  const currentRouteName = currentRouteId.replace("routes/", "");

  // Check if current route is in the hide list
  const isRouteInHideList = routesToHidePlayer.includes(currentRouteName);

  // Check if current route is a three-segment route that requires special handling
  const isThreeSegmentRoute = threeSegmentRoutes.includes(currentRouteName);
  const shouldShowPlayer = !isRouteInHideList && (isThreeSegmentRoute ? !searchParams.get("watch") && !!currentPlayVideo : !!currentPlayVideo);

  const { navigate } = useLocale();

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth < 768;
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Close function
  const handleOnCloseClick = useCallback(() => {
    dispatch(setCurrentVideo(null));
    dispatch(setCurrentVideoPosition(0));
    dispatch(setYoutubeVideoIsPlay(false));
    dispatch(setVideoQueue([]));
    sessionStorage.removeItem(VIDEO_PLAYER.CURRENT_VIDEO_POSITION);
    sessionStorage.removeItem(VIDEO_PLAYER.CURRENT_VIDEO);
    sessionStorage.removeItem(VIDEO_PLAYER.IS_VIDEO_PLAY);
    sessionStorage.removeItem(VIDEO_PLAYER.VIDEO_QUEUE);
    setVideo(null);
  }, [dispatch]);

  const handleOnFullScreen = () => {
    if (currentVideo) {
      navigate(currentVideo.from);
      dispatch(setFullScreen(true));
    }
  };

  const handleNextPlayVideo = () => {
    if (playListQueueData.length > 0) {
      const currentIndex = playListQueueData.findIndex((video) => video.videoId === currentPlayVideo.videoId);
      const nextIndex = (currentIndex + 1) % playListQueueData.length;
      const nextVideo = playListQueueData[nextIndex];
      dispatch(setCurrentVideo(nextVideo));
      dispatch(setCurrentVideoPosition(0));
    }
  };

  const handlePreviousVideo = () => {
    if (playListQueueData.length > 0) {
      const currentIndex = playListQueueData.findIndex((video) => video.videoId === currentPlayVideo.videoId);
      const previousIndex = currentIndex === 0 ? playListQueueData.length - 1 : currentIndex - 1;
      const previousVideo = playListQueueData[previousIndex];
      dispatch(setCurrentVideo(previousVideo));
      dispatch(setCurrentVideoPosition(0));
    }
  };

  const handlePlayPause = useCallback(
    (e?: React.MouseEvent | React.TouchEvent) => {
      // Prevent any event propagation that might close the popup
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (isVideoPlay) {
        dispatch(onPauseVideo());
      } else {
        dispatch(onPlayVideo(currentPlayVideo.videoId));
      }
    },
    [dispatch, isVideoPlay],
  );

  const handleMuteUnMute = useCallback(() => {
    const isMuted = isMute ? false : true;
    dispatch(setIsMute(isMuted));
  }, [dispatch, isMute]);

  useEffect(() => {
    setVideo(getSession(VIDEO_PLAYER.CURRENT_VIDEO) || null);
  }, []);

  useEffect(() => {
    currentVideoFromStore &&
      setVideo({
        videoId: currentVideoFromStore.videoId,
        from: currentVideoFromStore.from || "",
      });
  }, [currentVideoFromStore]);

  const isGlobalVideoPlay = shouldShowPlayer && pipMode;
  const isGlobalVideoPlayForMobile = shouldShowPlayer && pipMode;

  useEffect(() => {
    if (isGlobalVideoPlay) {
      dispatch(changeIsAudioPlayState(false));
    }

    if (isPlaying) {
      dispatch(setYoutubeVideoIsPlay(false));
      dispatch(changeIsAudioPlayState(true));
      dispatch(onPauseVideo());
      // removeVideoPlay();
    }
  }, [isGlobalVideoPlay, isPlaying]);

  return (
    <>
      {/* Desktop View */}
      {isGlobalVideoPlay && !isMobile && (
        <DesktopVideoPopup
          currentVideo={currentVideo}
          currentPlayVideo={currentPlayVideo}
          playListQueueData={playListQueueData}
          isVideoPlay={isVideoPlay}
          onClose={handleOnCloseClick}
          onFullScreen={handleOnFullScreen}
          onNextVideo={handleNextPlayVideo}
        />
      )}

      {/* Mobile View */}
      {isGlobalVideoPlayForMobile && isMobile && (
        <MobileVideoPopup
          currentVideo={currentVideo}
          currentPlayVideo={currentPlayVideo}
          playListQueueData={playListQueueData}
          isVideoPlay={isVideoPlay}
          onClose={handleOnCloseClick}
          onFullScreen={handleOnFullScreen}
          onNextVideo={handleNextPlayVideo}
          onPreviousVideo={handlePreviousVideo}
          onPlayPause={handlePlayPause}
          onHandleMuteUnMute={handleMuteUnMute}
          isMute={isMute}
        />
      )}
    </>
  );
}

export default GlobalVideoPlayer;
